import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Sidebar from '../landingPage/Sidebar';
import { FilterSelect } from '../../components/ui/select';
import { PrimaryButton } from '../../components/ui/button';
import { PrimaryTextField } from '../../components/ui/input';
import { PrimaryBreadcrumb } from '../../components/ui/breadcrumb';
import { TipBox } from '../../components/ui/info';
import { fetchProjects, addProject } from '../../state/slices/projectSlice';
import { PrimaryDialog } from '../../components/ui/dialog';
import { createProject } from '../../services/projectService';
import Cookies from 'js-cookie';

const steps = ['Project', 'Connections', 'Mapping', 'Canvas'];

const TemplateSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Get template data from location state
  const templateData = location.state?.template || null;
  const templateId = location.state?.templateId || null;

  const { list: projectsList } = useSelector((state) => state.projects);

  const [pipelineName, setPipelineName] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [pipelineNameError, setPipelineNameError] = useState('');
  const [projectError, setProjectError] = useState('');
  const [newProjectModalOpen, setNewProjectModalOpen] = useState(false);
  const [activeStep] = useState(0);

  useEffect(() => {
    if (!templateData && !templateId) {
      toast.error('Template information is missing. Please select a template again.');
      navigate('/pre-made-templates');
      return;
    }

    if (projectsList.length === 0) {
      dispatch(fetchProjects());
    }
  }, [dispatch, projectsList.length, templateData, templateId, navigate]);

  useEffect(() => {
    if (projectsList.length > 0 && !selectedProject) {
      const firstProject = projectsList[0];
      setSelectedProject(firstProject);
      setSelectedProjectName(firstProject.name);
    }
  }, [projectsList, selectedProject]);

  const handleProjectChange = (_, newValue) => {
    if (newValue) {
      const project = projectsList.find((p) => p.name === newValue);
      if (project) {
        setSelectedProject(project);
        setSelectedProjectName(newValue);
        setProjectError('');
      }
    } else {
      setSelectedProject(null);
      setSelectedProjectName('');
    }
  };

  const handleCreateNewProject = async (formData) => {
    const userId = Cookies.get('userId');
    const newProject = {
      user_id: userId,
      name: formData.name.trim(),
      description: formData.description.trim(),
      is_deleted: false,
    };

    const savedProject = await createProject(newProject);
    dispatch(addProject(savedProject));
    dispatch(fetchProjects());
    
    setSelectedProject(savedProject);
    setSelectedProjectName(savedProject.name);
    
    toast.success('Project created successfully!', {
      position: 'top-right',
      autoClose: 2000,
    });
  };

  const handleContinue = () => {
    let hasError = false;

    if (!pipelineName.trim()) {
      setPipelineNameError('Pipeline name is required');
      hasError = true;
    } else {
      setPipelineNameError('');
    }

    if (!selectedProject) {
      setProjectError('Please select a project');
      hasError = true;
    } else {
      setProjectError('');
    }

    if (hasError) {
      return;
    }

    // Navigate to connections configuration with template and project data
    navigate('/pre-made-templates/setup/connections', {
      state: {
        template: templateData,
        templateId: templateId,
        project: selectedProject,
        pipelineName: pipelineName.trim(),
      },
    });
  };

  const handleCancel = () => {
    if (templateId) {
      navigate(`/pre-made-templates/${templateId}`);
    } else {
      navigate('/pre-made-templates');
    }
  };

  const projectOptions = projectsList.map((project) => project.name);

  const templateTitle = templateData?.title || templateData?.name || 'Template';

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        bgcolor: '#FAFBFD',
        background: '#FAFBFD',
        minHeight: '100vh',
      }}
    >
      <Sidebar />

      <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <PrimaryBreadcrumb
          items={[
            {
              label: 'Pre-Made Templates',
              path: '/pre-made-templates',
            },
            {
              label: templateTitle,
              path: templateId ? `/pre-made-templates/${templateId}` : '/pre-made-templates',
            },
            {
              label: 'Project Setup',
            },
          ]}
        />

        {/* Step Indicator */}
        <Box sx={{ mb: 4, mt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: activeStep === steps.indexOf(label) ? 600 : 400,
                      fontSize: { xs: '0.9375rem', sm: '0.875rem', md: '1rem' },
                      color: activeStep === steps.indexOf(label) ? '#3B9562' : '#9CA3AF',
                    },
                    '& .MuiStepIcon-root': {
                      width: { xs: 25, sm: 26, md: 30 },
                      height: { xs: 25, sm: 26, md: 30 },
                      color: activeStep === steps.indexOf(label) ? '#10B981' : '#E5E7EB',
                      '&.Mui-active': {
                        color: '#3B9562',
                      },
                      '&.Mui-completed': {
                        color: '#3B9562',
                      },
                      '& .MuiStepIcon-text': {
                        fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box
          sx={{
            width: { xs: '100%', sm: '65%', md: '60%' },
            maxWidth: '800px',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: '8px',
              border: '1px solid #E6E6E6',
              background: '#FFFFFF',
              boxShadow: '0px 0px 6px 0px #7878781A',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 500,
                fontStyle: 'normal',
                fontSize: '1.25rem',
                lineHeight: '1.875rem',
                letterSpacing: '-0.45px',
                color: '#0A0A0A',
                mb: 1,
              }}
            >
              Select or Create Project
            </Typography>

            <Typography
              sx={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '0.875rem',
                lineHeight: '100%',
                letterSpacing: '-0.15px',
                color: '#4A5565',
                mb: 3,
              }}
            >
              Create a new project to organize your pipelines.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#383838',
                  mb: 1,
                }}
              >
                Project Name
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <FilterSelect
                    type="autocomplete"
                    options={projectOptions}
                    value={selectedProjectName}
                    onChange={handleProjectChange}
                    placeholder="Select Project"
                    sx={{
                      width: '100%',
                      background: '#FFFFFF',
                      '& .MuiAutocomplete-root': {
                        width: '100%',
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                      },
                    }}
                  />
                  {projectError && (
                    <Typography
                      sx={{
                        color: '#ef4444',
                        fontSize: '0.75rem',
                        mt: 0.5,
                        ml: 1.25,
                      }}
                    >
                      {projectError}
                    </Typography>
                  )}
                  {!projectError && (
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '0.8125rem',
                        lineHeight: '100%',
                        letterSpacing: '-0.15px',
                        color: '#4A5565',
                        mt: 0.5,
                      }}
                    >
                      Choose which project this pipeline belongs to
                    </Typography>
                  )}
                </Box>
                <PrimaryButton
                  variant="contained"
                  onClick={() => setNewProjectModalOpen(true)}
                  sx={{
                    minWidth: 'auto',
                  }}
                >
                  + New Project
                </PrimaryButton>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#383838',
                  mb: 1,
                }}
              >
                Pipeline Name
              </Typography>
              <PrimaryTextField
                fullWidth
                placeholder="e.g. Salesforce to Warehouse"
                value={pipelineName}
                onChange={(e) => {
                  setPipelineName(e.target.value);
                  if (pipelineNameError) {
                    setPipelineNameError('');
                  }
                }}
                error={!!pipelineNameError}
                helperText={pipelineNameError || 'Give your pipeline a descriptive name'}
              />
            </Box>

            <TipBox
              content="Projects help you organize related pipelines together. You can create multiple pipelines within the same project."
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                pt: 2,
                borderTop: '1px solid #E6E6E6',
              }}
            >
              <PrimaryButton
                variant="outlined"
                onClick={handleCancel}
              >
                Cancel
              </PrimaryButton>
              <PrimaryButton
                onClick={handleContinue}
                disabled={!pipelineName.trim() || !selectedProject}
              >
                Continue
              </PrimaryButton>
            </Box>
          </Paper>
        </Box>
      </Box>

      <PrimaryDialog
        open={newProjectModalOpen}
        onClose={() => setNewProjectModalOpen(false)}
        onSave={handleCreateNewProject}
        title="Create New Project"
        subtitle="Create a new project to organize your pipelines."
        submitButtonText="Create Project"
        loadingText="Creating..."
        fields={[
          {
            name: 'name',
            type: 'input',
            label: 'Project Name',
            placeholder: 'e.g. Sales Analytics',
            required: true,
            maxLength: 255,
          },
          {
            name: 'description',
            type: 'textarea',
            label: 'Description',
            placeholder: 'Enter workflow description (Optional)',
            multiline: true,
            rows: 4,
            required: false,
          },
        ]}
      />
    </Box>
  );
};

export default TemplateSetup;
